// M1 throwaway: shared analytics chat session state machine.
//
// Both useLiveAnalyticsChat and useCaseAnalyticsChat delegate to this hook.
// Domain-specific concerns (system prompt, tools, session key derivation)
// live in the caller; this hook owns the lifecycle.

import { useCallback, useEffect, useRef, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSessionStorage } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

import { resolveStructuredInput } from "src/analytics/copilot/resolveStructuredInput";
import {
  AWAITING_SESSION_TIMEOUT_MS,
  copilotSessionKeys,
  TERMINAL_STATUSES,
  useCopilotInvoke,
  useCopilotMessages,
} from "src/api/agents/copilot";
import { useCurrentWorkspace } from "src/api/queries";
import {
  Environment,
  type SessionMessagesResponseMessagesInner,
  SessionStatus,
} from "src/clients/agent-management";
import type { Environment as WorkspaceEnvironment } from "src/hooks/useEnvironment";
import { getLocalTimezone } from "src/utils/timezones";

type PersistedSession = {
  agentId: string;
  sessionId: string;
  /** `initialStructuredInput` has already been delivered on this session. */
  initialInputSent?: boolean;
};
type SessionMap = Record<string, PersistedSession>;

type TransientChatState =
  | { kind: "idle" }
  | {
      kind: "provisioning";
      pendingMessage: string | null;
      pendingStructuredInput?: Record<string, unknown> | null;
    }
  | { kind: "error"; message: string };

export type AnalyticsChatResult = {
  messages: SessionMessagesResponseMessagesInner[];
  isActive: boolean;
  isProvisioning: boolean;
  isReady: boolean;
  isExpired: boolean;
  failureMessage: string | null;
  isRestoringSession: boolean;
  /** Throws if the invoke fails so callers can react. Any `structuredInput`
   * is delivered exactly once, on the successful invoke. */
  send: (
    message: string,
    structuredInput?: Record<string, unknown>,
  ) => Promise<void>;
  resetSession: () => void;
};

export const useAnalyticsChatSession = ({
  storageKey,
  sessionSlotKey,
  environment,
  provision,
  agentVersionId: configuredAgentVersionId = "latest",
  provisionReady = true,
  sessionExpiryThresholdMs,
  sessionExpiredMessage,
  initialStructuredInput,
  injectCurrentDate = false,
}: {
  storageKey: string;
  sessionSlotKey: string | null;
  environment: WorkspaceEnvironment;
  provision: (baseUrl: string) => Promise<string>;
  /** Version to invoke for the session's agent. Defaults to its published version. */
  agentVersionId?: string;
  /** Gate eager provisioning until domain-specific data is ready. */
  provisionReady?: boolean;
  /** FE-side session expiry threshold in ms. Omit to disable expiry. */
  sessionExpiryThresholdMs?: number;
  /** Message shown when the session is expired. */
  sessionExpiredMessage?: string;
  /** `structured_input` for the first message of a session only; re-sending it every
   * turn would grow the thread and re-pay its tokens. A per-message `structuredInput`
   * wins on key collision. Pass `null` while it is still loading — sends buffer until
   * it arrives. */
  initialStructuredInput?: Record<string, unknown> | null;
  /** Put the client-local date in the agent's session context. */
  injectCurrentDate?: boolean;
}): AnalyticsChatResult => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;
  const queryClient = useQueryClient();

  const [sessions, _setSessions] = useSessionStorage<SessionMap>(
    storageKey,
    {},
  );

  const writeSession = useCallback(
    (id: string, session: PersistedSession | null) => {
      _setSessions((prev) => {
        if (session === null) {
          return Object.fromEntries(
            Object.entries(prev).filter(([k]) => k !== id),
          );
        }
        return { ...prev, [id]: session };
      });
    },
    [_setSessions],
  );

  const persistedSession =
    sessionSlotKey !== null ? (sessions[sessionSlotKey] ?? null) : null;

  const [transient, setTransient] = useState<TransientChatState>({
    kind: "idle",
  });

  const markInitialInputSent = useCallback(
    (slotKey: string, session: PersistedSession) => {
      _setSessions((prev) => {
        const current = prev[slotKey];
        if (
          current?.agentId !== session.agentId ||
          current?.sessionId !== session.sessionId
        ) {
          return prev;
        }
        return { ...prev, [slotKey]: { ...current, initialInputSent: true } };
      });
    },
    [_setSessions],
  );

  const agentId = persistedSession?.agentId ?? null;
  const sessionId = persistedSession?.sessionId ?? null;

  // Hold a message back only while the context is still owed; `null` means loading.
  const initialInputOwed =
    initialStructuredInput !== undefined &&
    persistedSession?.initialInputSent !== true;
  const canSend = !initialInputOwed || initialStructuredInput !== null;

  // Track current sessionSlotKey so the .catch() in the eager-provision
  // effect can ignore failures from a provision started for a stale slot.
  const sessionSlotKeyRef = useRef(sessionSlotKey);
  sessionSlotKeyRef.current = sessionSlotKey;

  // Side-effects (session write + error handling) live in the .then()/.catch()
  // of mutateAsync — not in onSuccess/onError — so in-flight provisions survive
  // provisionMutation.reset() when the user switches context.
  const provisionMutation = useMutation({
    mutationFn: (baseUrl: string) => provision(baseUrl),
  });

  const awaitingSessionRef = useRef(false);
  const awaitingTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Stops StrictMode's double-invoked effect from provisioning two agents.
  const eagerProvisionStartedRef = useRef(false);

  const messagesQueryKey = copilotSessionKeys.messages(sessionId);

  const invokeMutation = useCopilotInvoke({
    agentId: agentId ?? undefined,
    agentVersionId: agentId ? configuredAgentVersionId : undefined,
    sessionId,
    source: "analytics",
    environment:
      environment === "live" ? Environment.PRODUCTION : Environment.SANDBOX,
    extraInvokeData: {
      client_timezone: getLocalTimezone(),
      inject_current_date: injectCurrentDate,
    },
    messagesQueryKey,
    onInvokeStarted: () => {
      awaitingSessionRef.current = true;
      clearTimeout(awaitingTimeoutRef.current);
      awaitingTimeoutRef.current = setTimeout(() => {
        awaitingSessionRef.current = false;
      }, AWAITING_SESSION_TIMEOUT_MS);
    },
  });

  const clearTransientState = useCallback(() => {
    setTransient({ kind: "idle" });
    awaitingSessionRef.current = false;
    clearTimeout(awaitingTimeoutRef.current);
    eagerProvisionStartedRef.current = false;
    invokeMutation.reset();
    provisionMutation.reset();
  }, [invokeMutation.reset, provisionMutation.reset]);

  // Eager-provision on mount so the first message doesn't pay latency.
  // The `provisionReady` gate lets callers delay until domain-specific data
  // (e.g. flow version names) is loaded. Functional `setTransient` preserves
  // a message the user sent before provision started.
  useEffect(() => {
    if (
      !baseUrl ||
      sessionSlotKey === null ||
      agentId !== null ||
      !provisionReady
    )
      return;
    if (eagerProvisionStartedRef.current) return;
    eagerProvisionStartedRef.current = true;
    setTransient((prev) =>
      prev.kind === "provisioning"
        ? prev
        : { kind: "provisioning", pendingMessage: null },
    );
    const slotKey = sessionSlotKey;
    provisionMutation
      .mutateAsync(baseUrl)
      .then((newAgentId) => {
        writeSession(slotKey, { agentId: newAgentId, sessionId: uuidv4() });
      })
      .catch((err: unknown) => {
        // Ignore failures from a provision started for a slot the user has
        // already navigated away from.
        if (sessionSlotKeyRef.current !== slotKey) return;
        const message = err instanceof Error ? err.message : String(err);
        setTransient({
          kind: "error",
          message: `Provision failed: ${message}`,
        });
      });
  }, [
    baseUrl,
    sessionSlotKey,
    agentId,
    provisionReady,
    provisionMutation.mutateAsync,
  ]);

  // Reset transient state when the session identity changes.
  const [previousSlotKey, setPreviousSlotKey] = useState(sessionSlotKey);
  if (previousSlotKey !== sessionSlotKey) {
    setPreviousSlotKey(sessionSlotKey);
    clearTransientState();
  }

  // Once provisioning settles, fire the buffered first message, if any.
  useEffect(() => {
    if (
      transient.kind !== "provisioning" ||
      agentId === null ||
      sessionId === null ||
      !canSend
    )
      return;
    const { pendingMessage, pendingStructuredInput } = transient;
    setTransient({ kind: "idle" });
    if (pendingMessage !== null) {
      const { payload, carriesInitial } = resolveStructuredInput({
        initialStructuredInput,
        perMessage: pendingStructuredInput,
        alreadySent: persistedSession?.initialInputSent === true,
      });
      invokeMutation
        .mutateAsync({
          invocationMessage: pendingMessage,
          structuredInput: payload,
        })
        .then(() => {
          if (carriesInitial && sessionSlotKey !== null)
            markInitialInputSent(sessionSlotKey, { agentId, sessionId });
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when the provision settles or the gate opens
  }, [transient, agentId, sessionId, canSend]);

  const messagesQuery = useCopilotMessages(sessionId, {
    awaitingSessionRef,
    isPending: invokeMutation.isPending,
  });

  const rawMessages: SessionMessagesResponseMessagesInner[] = (
    messagesQuery.data?.pages.flatMap((p) => p.messages ?? []) ?? []
  ).toReversed();

  const sessionStatus = messagesQuery.data?.pages[0]?.session?.status;
  const isSessionTerminal =
    sessionStatus != null && TERMINAL_STATUSES.has(sessionStatus);

  const sessionCreatedAt = messagesQuery.data?.pages[0]?.session?.created_at;
  const isSessionExpired =
    sessionExpiryThresholdMs != null &&
    sessionCreatedAt != null &&
    Date.now() - new Date(sessionCreatedAt).getTime() >
      sessionExpiryThresholdMs;

  const send = useCallback(
    async (
      message: string,
      structuredInput?: Record<string, unknown>,
    ): Promise<void> => {
      const trimmed = message.trim();
      if (!trimmed || !baseUrl || isSessionExpired) return;

      if (
        canSend &&
        agentId !== null &&
        sessionId !== null &&
        transient.kind === "idle"
      ) {
        const { payload, carriesInitial } = resolveStructuredInput({
          initialStructuredInput,
          perMessage: structuredInput,
          alreadySent: persistedSession?.initialInputSent === true,
        });
        await invokeMutation.mutateAsync({
          invocationMessage: trimmed,
          structuredInput: payload,
        });
        if (carriesInitial && sessionSlotKey !== null)
          markInitialInputSent(sessionSlotKey, { agentId, sessionId });
        return;
      }

      // Don't pile onto a failed provision.
      if (transient.kind === "error") return;

      setTransient({
        kind: "provisioning",
        pendingMessage: trimmed,
        pendingStructuredInput: structuredInput,
      });
    },
    [
      baseUrl,
      agentId,
      sessionId,
      transient,
      invokeMutation.mutateAsync,
      isSessionExpired,
      canSend,
      initialStructuredInput,
      persistedSession,
      sessionSlotKey,
      markInitialInputSent,
    ],
  );

  const resetSession = useCallback(() => {
    if (transient.kind === "provisioning") return;
    if (sessionSlotKey === null) return;
    clearTransientState();
    writeSession(sessionSlotKey, null);
    queryClient.removeQueries({ queryKey: messagesQueryKey });
  }, [
    clearTransientState,
    transient,
    sessionSlotKey,
    writeSession,
    queryClient,
    messagesQueryKey,
  ]);

  const isAwaitingFirstPoll =
    awaitingSessionRef.current && sessionStatus == null;

  // A user is waiting once a message is buffered onto an in-flight provision.
  // A bare eager provision must not make the UI look busy.
  const hasPendingMessage =
    transient.kind === "provisioning" && transient.pendingMessage !== null;

  const isActive =
    hasPendingMessage ||
    invokeMutation.isPending ||
    isAwaitingFirstPoll ||
    (sessionStatus != null && !isSessionTerminal);

  // A restored session's history loads on open with nothing "active" yet — show
  // a skeleton over the empty state. `!provisionMutation.isSuccess` excludes a
  // freshly-provisioned new chat: its empty session also fetches (and 404s to
  // empty), but should show the new-conversation prompts, not the skeleton.
  const isRestoringSession =
    sessionId !== null &&
    messagesQuery.isLoading &&
    !isActive &&
    !provisionMutation.isSuccess;

  const invokeErrorMessage = invokeMutation.isError
    ? invokeMutation.error.message
    : null;

  const sessionFailedMessage =
    sessionStatus === SessionStatus.FAILED
      ? messagesQuery.data?.pages[0]?.session?.termination_reason
        ? `Something went wrong: ${messagesQuery.data.pages[0].session.termination_reason}`
        : "Something went wrong"
      : null;

  const transientError = transient.kind === "error" ? transient.message : null;
  const expiredError = isSessionExpired
    ? (sessionExpiredMessage ?? null)
    : null;

  const failureMessage =
    transientError ??
    expiredError ??
    invokeErrorMessage ??
    sessionFailedMessage;

  return {
    messages: rawMessages,
    isActive,
    isProvisioning:
      (provisionMutation.isPending || !canSend) && hasPendingMessage,
    isReady: baseUrl != null && sessionSlotKey != null,
    isExpired: isSessionExpired,
    failureMessage,
    isRestoringSession,
    send,
    resetSession,
  };
};
